import { Component, type ErrorInfo, type PropsWithChildren, type ReactNode } from "react";
import { ShieldAlert } from "lucide-react";

type State = { error: Error | null };

/**
 * `useQuery` et `usePaginatedQuery` relancent les erreurs serveur pendant le rendu : sans filet,
 * une entrée d'URL invalide ou une perte de droits en cours de session démonte toute l'application
 * et laisse une page blanche. Ce garde-fou conserve une issue à l'utilisateur.
 */
export class ErrorBoundary extends Component<PropsWithChildren<{ onReset?: () => void }>, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("Erreur non rattrapée dans l’interface", error, info.componentStack);
  }

  private reset = () => {
    this.setState({ error: null });
    this.props.onReset?.();
  };

  render(): ReactNode {
    if (!this.state.error) return this.props.children;
    return (
      <div className="error-screen" role="alert">
        <span className="error-screen__icon"><ShieldAlert size={26} /></span>
        <h1>Cette page n’a pas pu s’afficher</h1>
        <p>
          L’action a été interrompue. Vos données ne sont pas affectées : rien n’a été enregistré.
          Réessayez, ou revenez à la vue d’ensemble.
        </p>
        <div className="error-screen__actions">
          <button type="button" className="button button--primary" onClick={this.reset}>Réessayer</button>
          <a className="button button--secondary" href="/">Vue d’ensemble</a>
        </div>
      </div>
    );
  }
}
